import { usePageMeta } from "@/lib/seo";
import { IMAGES, PRINCIPLES, SITE } from "@/content/site";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionHeader, CtaLink, IconTile } from "@/components/site/primitives";
import { CtaBand } from "@/components/site/CtaBand";

const FOUNDER_NOTE =
  "SwastSaathi is building an impact-driven, commercially sustainable healthcare venture for rural India, starting with Bihar. By connecting underserved communities with verified doctors, diagnostic centres, pharmacies, and local health partners, we aim to make quality healthcare more accessible and affordable while creating revenue through service commissions, healthcare partnerships, and telemedicine. Our vision is to combine measurable social impact with a scalable, financially sustainable business model.";

export default function AboutPage() {
  usePageMeta({
    title: "About SwastSaathi — Our Story, Mission & Founder",
    description: "Why SwastSaathi exists: our founding motivation, mission, vision and operating principles for community-based primary healthcare in rural India.",
    path: "/about",
  });
  return (
    <>
      <PageHero
        testId="about-hero"
        eyebrow="About SwastSaathi"
        title="Building the first step of care, close to where people live."
        intro="SwastSaathi is an idea-stage health-tech venture. We are preparing to validate a community-based, partnership-led model for primary healthcare in rural India — starting with Bihar."
        image={IMAGES.fieldWoman}
        imageAlt="A woman standing in a green field holding a plant"
      />

      <section data-testid="about-story" className="bg-white py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <SectionHeader className="lg:col-span-5" eyebrow="Our story" title="Started from a simple question: why is basic care still so far away?" />
          <Reveal delay={0.1} className="space-y-5 text-[17px] leading-relaxed text-slate lg:col-span-7">
            <p>In many villages, a fever, a persistent cough or a blood-pressure check can mean a long trip to a distant town, an uncertain wait and a lost day of income. Tests and medicines are often found in different places, and follow-up is easy to miss.</p>
            <p>SwastSaathi was founded to explore a practical answer: a trusted, local access point that helps people reach qualified professionals and essential services — without trying to replace the doctors, laboratories, pharmacies and hospitals that already serve the region.</p>
            <p>We are at the beginning. Before scaling anything, we intend to listen to communities, work with licensed providers and test the model carefully through a focused pilot in Bihar.</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-problem" className="bg-mint py-20 lg:py-28">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2">
          <Reveal><img src={IMAGES.village} alt="A village street in India with a woman on a bicycle and a cow beside a blue house" loading="lazy" className="aspect-[4/3] w-full rounded-[1.75rem] object-cover" /></Reveal>
          <div>
            <SectionHeader eyebrow="The accessibility problem" title="The gap is not only clinical — it is distance, cost and coordination." intro="Rural communities often face long travel to qualified professionals, fragmented access to consultations, diagnostics and medicines, delayed treatment and weak continuity of care for chronic conditions. Each barrier adds cost and makes people more likely to delay care." />
            <Reveal delay={0.1} className="mt-8"><CtaLink to="/healthcare-model" testId="about-model-cta" variant="secondary">How our model responds</CtaLink></Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-mission-vision" className="bg-white py-20 lg:py-28">
        <div className="container-x grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="card-soft h-full p-8 sm:p-10">
              <p className="eyebrow">Our mission</p>
              <p className="mt-5 font-display text-2xl font-medium leading-snug text-plum sm:text-[1.7rem]">To make primary healthcare more accessible, affordable, coordinated and convenient for underserved communities — by connecting them with licensed providers through trusted local access points.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl bg-plum p-8 sm:p-10">
              <p className="eyebrow text-teal-100">Our vision</p>
              <p className="mt-5 font-display text-2xl font-medium leading-snug text-white sm:text-[1.7rem]">A future where quality primary healthcare is closer to every community in India — through a trusted, connected network that strengthens existing healthcare infrastructure.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-principles" className="bg-mint py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="Operating principles" title="Six principles that shape every decision." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.07}>
                <article data-testid={`principle-${i}`} className="card-soft card-soft-hover h-full p-7">
                  <IconTile icon={p.icon} tone={i % 2 ? "plum" : "teal"} />
                  <h3 className="mt-6 text-xl font-semibold">{p.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate">{p.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="founder" data-testid="about-founder" className="bg-white py-20 lg:py-28">
        <div className="container-x grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative mx-auto aspect-square w-full max-w-[320px]">
              <div className="absolute inset-0 rounded-[2rem] bg-mint" />
              <div className="absolute inset-6 flex items-center justify-center rounded-full border border-teal/25 bg-white shadow-[0_30px_60px_-30px_rgba(44,15,101,0.4)]" data-testid="founder-monogram">
                <span className="font-display text-7xl font-semibold tracking-tight text-plum">A<span className="text-teal-700">K</span></span>
                <span className="absolute right-10 top-10 h-3 w-3 rounded-full bg-magenta" />
              </div>
            </div>
            <p className="mt-5 text-center font-display text-lg font-semibold text-plum" data-testid="founder-name">{SITE.founder}, Founder</p>
          </Reveal>
          <div className="lg:col-span-8 lg:pl-8">
            <SectionHeader eyebrow="Founder" title="A note from Aman Kumar" />
            <Reveal delay={0.1}>
              <blockquote className="mt-8 border-l-2 border-magenta pl-6 text-[17px] leading-relaxed text-slate md:text-lg" data-testid="founder-note">
                {FOUNDER_NOTE}
              </blockquote>
              <div className="mt-9 flex flex-wrap gap-3">
                <CtaLink to="/contact" testId="founder-contact-cta">Contact SwastSaathi</CtaLink>
                <CtaLink to="/investors" testId="founder-investors-cta" variant="secondary">Investor overview</CtaLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand testId="about-cta" />
    </>
  );
}
